import { errorMessage } from './importRows';
import type { ExistingCdmoDomainMatch, ExistingCdmoFeiMatch, ValidatedCdmoImportRow } from './validateRow';

/** The two database lookups the preview runs against existing profiles. */
export interface ExistingProfileLookups {
  findByDomains(domains: string[]): Promise<ExistingCdmoDomainMatch[]>;
  findByFeiNumbers(feiNumbers: string[]): Promise<ExistingCdmoFeiMatch[]>;
}

/** What the preview's lookups found, and why any of them could not run (null when both did). */
export interface ExistingProfiles {
  domainMatches: ExistingCdmoDomainMatch[];
  feiMatches: ExistingCdmoFeiMatch[];
  failure: string | null;
}

/**
 * Looks up the existing profiles that the uploaded rows' website domains and FEI numbers belong to.
 * The two lookups run independently: when one fails, the other's results are still shown, and the
 * failure is reported so the admin knows the preview is incomplete. Each row is checked again just
 * before it is created either way.
 *
 * @param lookups - The database lookups
 * @param rows - The validated rows; a malformed row's FEI numbers sit under the wrong column and are not looked up
 * @returns The matches from each lookup that succeeded, and the distinct failure messages, joined
 */
export async function lookUpExistingProfiles(
  lookups: ExistingProfileLookups,
  rows: readonly ValidatedCdmoImportRow[]
): Promise<ExistingProfiles> {
  const domains = rows.map((row) => row.normalizedDomain).filter((domain): domain is string => Boolean(domain));
  const feiNumbers = [...new Set(rows.filter((row) => !row.malformed).flatMap((row) => row.profile.fei_numbers))];

  const [domainLookup, feiLookup] = await Promise.allSettled([
    lookups.findByDomains(domains),
    feiNumbers.length > 0 ? lookups.findByFeiNumbers(feiNumbers) : Promise.resolve([]),
  ]);

  const failures = [domainLookup, feiLookup].flatMap((result) => (result.status === 'rejected' ? [errorMessage(result.reason)] : []));
  return {
    domainMatches: domainLookup.status === 'fulfilled' ? domainLookup.value : [],
    feiMatches: feiLookup.status === 'fulfilled' ? feiLookup.value : [],
    failure: failures.length > 0 ? [...new Set(failures)].join('; ') : null,
  };
}
